/**
 * Generic Error Report CSV Serializer & Browser Download Trigger
 *
 * Produces RFC 4180-compliant CSV Blobs from a generic row shape.
 * Reusable by both the preview error report (BatchValidationResult mapping)
 * and the future import error report (server-side rejection mapping).
 *
 * This module is pure (except triggerBrowserDownload which touches the DOM),
 * synchronous, and side-effect-free for the serializer.
 */

/** Generic row shape accepted by the CSV serializer */
export interface ErrorReportRow {
  /** 1-based row index from the original file */
  rowIndex: number;
  /** Column key → cell value (transformed). Missing keys → empty string in output */
  data: Record<string, string>;
  /** Machine-readable error code(s) for this row */
  errorCode: string;
  /** Human-readable error description for this row */
  errorMessage: string;
}

/** Generic column definition for the CSV serializer */
export interface ErrorReportColumn {
  /** Column key matching keys in ErrorReportRow.data */
  key: string;
  /** Human-readable column header label */
  label: string;
}

/** Characters reserved by common filesystems */
const RESERVED_FILENAME_CHARS = /[/\\:*?"<>|]/g;

/**
 * Escapes a field value per RFC 4180:
 * - If the value contains a comma, double-quote, or newline, wrap in double quotes
 * - Internal double-quotes are escaped by doubling them
 */
function escapeField(value: string): string {
  if (
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Produces a RFC 4180-compliant CSV Blob from generic error report data.
 *
 * Output column order: "Row" | ...columns[].label | "Error Code" | "Error Message"
 * - Prepends UTF-8 BOM (\uFEFF) for Excel compatibility
 * - Uses CRLF (\r\n) line terminators
 * - Quotes fields containing commas, double-quotes, or newlines
 * - Empty input (zero rows) produces headers-only CSV (no throw)
 *
 * @param rows - Error report rows to serialize
 * @param columns - Column definitions determining data column order and headers
 * @returns Blob with MIME type text/csv;charset=utf-8
 */
export function generateErrorReportCSV(
  rows: ErrorReportRow[],
  columns: ErrorReportColumn[],
): Blob {
  const headers = [
    'Row',
    ...columns.map((c) => c.label),
    'Error Code',
    'Error Message',
  ];

  const headerLine = headers.map(escapeField).join(',');

  const dataLines = rows.map((row) => {
    const fields = [
      String(row.rowIndex),
      ...columns.map((c) => row.data[c.key] ?? ''),
      row.errorCode,
      row.errorMessage,
    ];
    return fields.map(escapeField).join(',');
  });

  const csv = '\uFEFF' + [headerLine, ...dataLines].join('\r\n') + '\r\n';

  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}

/**
 * Triggers a browser file download using the anchor-click pattern.
 *
 * Creates an object URL from the blob, attaches it to a temporary <a> element
 * with the given filename as the download attribute, programmatically clicks it,
 * then schedules URL revocation via setTimeout(0) to avoid browser race conditions.
 *
 * Filename is sanitized: trimmed, filesystem-reserved characters removed.
 *
 * @param blob - The file content to download
 * @param filename - Desired filename (sanitized internally)
 * @throws If blob is null/undefined, filename is empty after sanitization, or URL creation fails
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  if (!blob) {
    throw new Error('A valid Blob is required');
  }

  const sanitized = filename.trim().replace(RESERVED_FILENAME_CHARS, '');

  if (!sanitized) {
    throw new Error('A non-empty filename is required');
  }

  let url: string;
  try {
    url = URL.createObjectURL(blob);
  } catch {
    throw new Error('Failed to create object URL');
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = sanitized;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(url), 0);
}
